/**
 * 📋 SDLC 6.5.0 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/components/ui/TimeTextInput.tsx
 * PURPOSE: Drop-in replacement for `<input type="time">` — the native browser
 *          widget renders an OS/locale-controlled hour/minute/AM-PM picker (on
 *          Windows+Chrome with Vietnamese locale, columns show "SA"/"CH" in an
 *          order the app has zero control over, since it's the browser's own
 *          UI, not ours). This component keeps the "bấm để chọn" convenience
 *          (a click-to-pick dropdown, not text-only) but with a self-built
 *          24h Giờ/Phút picker — no AM/PM concept at all, matching the 24h
 *          convention already used across SC-009/SC-011 — so the SA/CH
 *          ordering question doesn't even arise.
 * SDLC FRAMEWORK VERSION: 6.5.0 (7-Pillar + 9 Mental Models + Long-Running Agent Protocol)
 * DATE: 2026-09-07
 * COMPLIANCE: SDLC 6.5.0 + Sprint Governance Rule 9
 * PILLAR: P1 (Stage 06 - BUILD) + P6 (UI/UX Design)
 *
 * @business-context
 * - Domain: Bất kỳ màn hình nào cần nhập giờ (chấm công, phân ca, cấu hình giờ làm việc)
 * - Impact: Loại bỏ phụ thuộc vào picker gốc của trình duyệt/OS (không đồng nhất
 *   giữa máy/trình duyệt/locale), vẫn giữ được thao tác "bấm chọn" thay vì chỉ gõ chữ
 * - Stakeholders: Quản lý chi nhánh, Tenant Admin — mọi vai trò nhập giờ thủ công
 *
 * @technical-architecture
 * - Layer: UI Components (dùng chung nhiều màn)
 * - Pattern: input text (gõ tự do, parse-on-blur/Enter) + dropdown portal 2 cột
 *   Giờ(00-23)/Phút(00-59) bấm chọn trực tiếp — cùng kỹ thuật portal + fixed
 *   position + click-outside/Escape đã có ở PortalMenu.tsx (F-6/F-7/F-8), viết
 *   riêng (không sửa PortalMenu dùng chung) vì cần neo trái, PortalMenu chỉ neo phải
 * - Dependencies: React, react-dom (createPortal)
 *
 * @cross-references
 * - Tiền lệ cùng cách parse giờ gõ tay: docs/02-design/04-ui-ux-design/design-imports/
 *   ui-ux-nqhbot/v0/schedule/manager/sc011-build-prototype.html `normHM()`
 * - Tiền lệ portal-positioning: frontend/src/components/common/PortalMenu.tsx
 * - Nơi dùng: ManagerAttendancePage.tsx, ManagerTasksPage.tsx,
 *   ManagerStaffPage.tsx, TenantBotConfig.tsx, TenantSettings.tsx (thay hết
 *   `<input type="time">` — phát hiện 2026-09-07: picker gốc trình duyệt hiện
 *   "CH" trước "SA" theo locale, không sửa được bằng CSS/code)
 * - 2026-09-07 (bổ sung, user yêu cầu): bản đầu bỏ hẳn thao tác "chọn" (chỉ gõ
 *   chữ) — user muốn GIỮ LẠI thao tác bấm-chọn, chỉ cần đúng thứ tự/không phụ
 *   thuộc OS. Thêm dropdown 2 cột Giờ/Phút tự dựng (24h, không có SA/CH).
 */

import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

/** Chuẩn hoá chuỗi giờ gõ tự do thành "HH:MM" 24h, hoặc "" nếu không hợp lệ/rỗng.
 * Chấp nhận: "1405", "14:05", "14.05", "14h05", "14 05", "14" (→ "14:00").
 * Cùng logic với `normHM()` đã dùng ở prototype SC-011/SC-009 — giữ nhất quán. */
export function normalizeTimeText(raw: string): string {
  const v = String(raw ?? "").trim();
  if (!v) return "";
  const m = v.match(/^(\d{1,2})[:.hH ]?(\d{2})$/) || v.match(/^(\d{1,2})$/);
  if (!m) return "";
  const hh = Number(m[1]);
  const mm = m[2] ? Number(m[2]) : 0;
  if (hh > 23 || mm > 59) return "";
  return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
}

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
const MINUTES = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, "0"));

export interface TimeTextInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "type" | "value" | "onChange"> {
  /** "HH:MM" 24h, hoặc "" — cùng format `<input type="time">` vẫn dùng, để chỗ gọi
   * không cần đổi cách lưu state. */
  value: string;
  /** Nhận đúng shape `{ target: { value } }` như sự kiện input gốc — chỗ gọi hiện tại
   * dùng `(e) => setX(e.target.value)` không cần sửa gì khi đổi sang component này. */
  onChange: (e: { target: { value: string } }) => void;
}

/** Dropdown 2 cột Giờ/Phút — bấm chọn trực tiếp, portal ra `document.body` +
 * position:fixed neo theo rect của input (tránh bị modal `overflow:hidden`
 * cắt đứng — cùng vấn đề PortalMenu.tsx đã giải quyết). */
const TimeDropdown: React.FC<{
  anchorRect: DOMRect;
  hour: string;
  minute: string;
  onPickHour: (h: string) => void;
  onPickMinute: (m: string) => void;
  onClose: () => void;
}> = ({ anchorRect, hour, minute, onPickHour, onPickMinute, onClose }) => {
  const ref = useRef<HTMLDivElement>(null);
  const hourListRef = useRef<HTMLDivElement>(null);
  const minuteListRef = useRef<HTMLDivElement>(null);
  const [flipUp, setFlipUp] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const h = el.offsetHeight;
    const overflowDown = anchorRect.bottom + 4 + h > window.innerHeight;
    const roomUp = anchorRect.top - 4 - h > 0;
    setFlipUp(overflowDown && roomUp);
    // Cuộn giá trị đang chọn vào giữa khung nhìn ngay khi mở — giống hành vi
    // "nhảy tới ô đang chọn" của picker gốc trình duyệt.
    // Guard `?.scrollIntoView?.` (không chỉ optional-chain phần tử) — JSDOM
    // (môi trường test) không cài đặt scrollIntoView, throw nếu gọi thẳng.
    hourListRef.current?.querySelector('[data-selected="true"]')?.scrollIntoView?.({ block: "center" });
    minuteListRef.current?.querySelector('[data-selected="true"]')?.scrollIntoView?.({ block: "center" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onDocMouseDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    // Chỉ đóng khi TRANG (hoặc modal cha) cuộn — cuộn BÊN TRONG chính 2 cột
    // Giờ/Phút (168px không đủ hiện hết 24 giờ / 60 phút, cuộn nội bộ là thao
    // tác bình thường để tìm giá trị) không được tính là "cuộn ra ngoài".
    // Bug thật 2026-09-07 (phát hiện qua verify Playwright trên app sống):
    // dùng capture phase (`true`) để bắt cuộn từ modal cha, nhưng capture cũng
    // bắt luôn cuộn của chính danh sách bên trong dropdown → cuộn để thấy giờ
    // 14 (nằm dưới màn hình ban đầu) tự đóng dropdown ngay trước khi bấm được.
    const onScrollResize = (e: Event) => {
      if (ref.current && e.target instanceof Node && ref.current.contains(e.target)) return;
      onClose();
    };
    document.addEventListener("mousedown", onDocMouseDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScrollResize, true);
    window.addEventListener("resize", onScrollResize);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScrollResize, true);
      window.removeEventListener("resize", onScrollResize);
    };
  }, [onClose]);

  const pos: React.CSSProperties = flipUp
    ? { bottom: window.innerHeight - anchorRect.top + 4, left: anchorRect.left }
    : { top: anchorRect.bottom + 4, left: anchorRect.left };

  const col = (
    label: string,
    items: string[],
    current: string,
    onPick: (v: string) => void,
    listRef: React.Ref<HTMLDivElement>,
  ) => (
    <div style={{ display: "flex", flexDirection: "column", width: 64 }}>
      <div
        style={{
          textAlign: "center",
          fontSize: 11,
          fontWeight: 600,
          color: "#6b7280",
          padding: "4px 0",
          borderBottom: "1px solid #e5e7eb",
        }}
      >
        {label}
      </div>
      <div ref={listRef} style={{ maxHeight: 168, overflowY: "auto" }}>
        {items.map((v) => (
          <button
            key={v}
            type="button"
            data-selected={v === current}
            // "07" vừa là giờ vừa là phút — aria-label riêng theo cột để trình
            // đọc màn hình (và test) phân biệt được, không chỉ dựa vào text "07".
            aria-label={`${label} ${v}`}
            onMouseDown={(e) => e.preventDefault()} // giữ focus ở input, khỏi trigger blur trước khi commit
            onClick={() => onPick(v)}
            style={{
              display: "block",
              width: "100%",
              padding: "6px 0",
              border: "none",
              background: v === current ? "#2563eb" : "transparent",
              color: v === current ? "#fff" : "#111827",
              fontWeight: v === current ? 700 : 400,
              cursor: "pointer",
              fontSize: 13,
              textAlign: "center",
            }}
          >
            {v}
          </button>
        ))}
      </div>
    </div>
  );

  return createPortal(
    <div
      ref={ref}
      role="listbox"
      aria-label="Chọn giờ"
      onMouseDown={(e) => e.stopPropagation()}
      style={{
        position: "fixed",
        ...pos,
        zIndex: 2000,
        display: "flex",
        gap: 1,
        background: "#fff",
        border: "1px solid #d1d5db",
        borderRadius: 8,
        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
        overflow: "hidden",
      }}
    >
      {col("Giờ", HOURS, hour, onPickHour, hourListRef)}
      {col("Phút", MINUTES, minute, onPickMinute, minuteListRef)}
    </div>,
    document.body,
  );
};

export const TimeTextInput = React.forwardRef<HTMLInputElement, TimeTextInputProps>(
  ({ value, onChange, onBlur, onKeyDown, onFocus, onClick, ...rest }, ref) => {
    const [draft, setDraft] = useState(value ?? "");
    const [focused, setFocused] = useState(false);
    const [showPicker, setShowPicker] = useState(false);
    const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);
    const innerRef = useRef<HTMLInputElement | null>(null);

    // Đồng bộ lại khi giá trị từ ngoài đổi (vd nút "Ghi nhận theo ca" điền giùm),
    // không chỉ khi người dùng tự gõ.
    useEffect(() => {
      setDraft(value ?? "");
    }, [value]);

    const commit = (next?: string) => {
      const normalized = normalizeTimeText(next ?? draft);
      setDraft(normalized);
      if (normalized !== value) onChange({ target: { value: normalized } });
      return normalized;
    };

    const openPicker = (el: HTMLInputElement) => {
      setAnchorRect(el.getBoundingClientRect());
      setShowPicker(true);
    };

    const currentHour = draft.slice(0, 2) || "00";
    const currentMinute = draft.slice(3, 5) || "00";

    return (
      <div style={{ display: "inline-block", position: "relative" }}>
        <input
          ref={(node) => {
            innerRef.current = node;
            if (typeof ref === "function") ref(node);
            else if (ref) (ref as unknown as React.MutableRefObject<HTMLInputElement | null>).current = node;
          }}
          type="text"
          inputMode="numeric"
          // Bug thật 2026-09-07 (user báo, ảnh chụp "Ra được tính" trông như
          // "mất"): rỗng + KHÔNG focus → KHÔNG hiện placeholder (giống input
          // type=time cũ khi rỗng gần như trắng trơn). Chỉ gợi ý khi đang focus.
          placeholder={focused ? "HH:MM" : ""}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onFocus={(e) => {
            setFocused(true);
            openPicker(e.currentTarget);
            onFocus?.(e);
          }}
          onClick={(e) => {
            openPicker(e.currentTarget);
            onClick?.(e);
          }}
          onBlur={(e) => {
            setFocused(false);
            commit();
            // Đóng picker ở đây nếu blur KHÔNG phải do bấm vào chính dropdown
            // (dropdown đã stopPropagation mousedown nên document click-outside
            // của nó tự lo phần "bấm ra ngoài" — ở đây chỉ đóng khi rời hẳn input
            // bằng Tab/click nơi khác ngoài dropdown).
            setShowPicker(false);
            onBlur?.(e);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              commit();
              (e.target as HTMLInputElement).blur();
            }
            if (e.key === "Escape") setShowPicker(false);
            onKeyDown?.(e);
          }}
          {...rest}
        />
        {showPicker && anchorRect && !rest.disabled && (
          <TimeDropdown
            anchorRect={anchorRect}
            hour={currentHour}
            minute={currentMinute}
            onPickHour={(h) => {
              const next = `${h}:${currentMinute}`;
              setDraft(next);
              commit(next);
              innerRef.current?.focus();
            }}
            onPickMinute={(m) => {
              const next = `${currentHour}:${m}`;
              setDraft(next);
              commit(next);
              innerRef.current?.focus();
            }}
            onClose={() => setShowPicker(false)}
          />
        )}
      </div>
    );
  },
);

TimeTextInput.displayName = "TimeTextInput";
